import React, { forwardRef, useId } from 'react';

import styled from '@mint-lib/styled';

import FormControlLabelM, {
  Props as FormControlLabelProps,
} from '../FormControlLabel/FormControlLabel.jsx';
import Switch, { SwitchProps } from '../Switch/Switch.jsx';
import Typography from '../Typography/Typography.jsx';

export interface SwitchWithLabelProps {
  /**
   * If `true`, the checkbox is checked.
   */
  checked?: boolean;
  /**
   * If `true`, the checkbox will be disabled.
   */
  disabled?: boolean;
  /**
   * The label text.
   */
  label?: string;
  /**
   * The value of the component.
   */
  value?: string;
  /**
   * The size of the component. small is equivalent to the dense checkbox styling.
   */
  size?: SwitchProps['size'];
  /**
   * Change event
   */
  onChange?: SwitchProps['onChange'];
  /**
   * The position of the label.
   */
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom';
  /**
   * A control element. For instance, it can be a `Radio`, a `Switch` or a `Checkbox`.

   */
  formControlProps?: FormControlLabelProps;
  /**
   * The color of the component. It supports those theme colors that make sense for this component.

   */
  color?: SwitchProps['color'];
  /**
   * Show the on/off text
   * @default false
   */
  withText?: boolean;
  /**
   * Text appear if checked true and withText is true
   */
  checkedText?: string;
  /**
   * Text appear if checked false and withText is true
   */
  unCheckedText?: string;
}

/**
 *
 * @deprecated Use Switch and Label from @mint-lib/tw-components instead
 */
const SwitchWithLabel = forwardRef(
  (
    {
      labelPlacement = 'top',
      checkedText = 'on',
      unCheckedText = 'off',
      ...props
    }: SwitchWithLabelProps,
    ref,
  ) => {
    // Unique Id
    const id = useId();
    return (
      <StyledFormControlLabel
        {...props.formControlProps}
        htmlFor={id}
        labelPlacement={labelPlacement}
        control={
          <Switch
            ref={ref}
            id={id}
            inputProps={{ role: 'switch', 'aria-checked': props.checked }}
            size={props.size}
            color={props.color}
            withText={props.withText}
            onChange={props.onChange}
            checked={props.checked}
            disabled={props.disabled}
            checkedText={checkedText}
            unCheckedText={unCheckedText}
          />
        }
        label={
          <StyledTypography variant="label01" disabled={props.disabled}>
            {props.label}
          </StyledTypography>
        }
      />
    );
  },
);

export default SwitchWithLabel;

const StyledTypography = styled(Typography)<{ disabled?: boolean }>(
  ({ theme, disabled }) => ({
    color: disabled
      ? theme.palette.text.disabled
      : theme.palette.text.secondary,
    letterSpacing: 0.32,
  }),
);

const StyledFormControlLabel = styled(FormControlLabelM)`
  gap: 8px;
  width: fit-content;
  align-items: ${({ labelPlacement }) =>
    (labelPlacement === 'start' || labelPlacement === 'end') && 'center'};
`;
